import React, { useCallback, useContext, useMemo, useState } from 'react';
import { bulkSetProxy, type DetailedProxyMetadata, removeFundProxy } from 'venn-api';
import { Icon, Loading, Notifications, NotificationType, ShimmerBlock } from 'venn-ui-kit';
import { invalidate, useHasFF, withSuspense } from 'venn-utils';
import { cn } from '@/lib/utils';
import { useProxyDataQuery } from '../../../hooks';
import SidePanelOverlay from '../../../side-panel-overlay/SidePanelOverlay';
import type { ProxyMetaWithName } from '../proxy-picker/BulkProxyPicker';
import SingleInvestmentInfo from './components/SingleInvestmentInfo';
import HoldingsProxyInfoIcon from './components/HoldingsProxyInfoIcon';
import HoldingsProxySearch from './components/HoldingsProxySearch';
import HoldingsProxySummary from './components/HoldingsProxySummary';
import type { FundToBulkProxy, FundToProxy } from '../types';
import type { SelectedProxy } from './types';
import { analysisViewRefIdState, proxySetIdState } from 'venn-state';
import { useRecoilValue } from 'recoil';
import { ProxyPickerHeader } from '../proxy-picker/components/ProxyPickerHeaderPrimitives';
import { isNil } from 'lodash';

const HEADER_TITLE = 'Proxy holdings';
const MANAGE_DATA_SUBTITLE = 'Proxies will be applied across your entire workspace.';

const BulkHoldingsProxyPickerInternal = ({
  investments,
  onProxyChange,
}: {
  investments: (FundToProxy | FundToBulkProxy)[];
  onProxyChange?: (appliedProxies: ProxyMetaWithName[], failedToProxyFunds: (FundToProxy | FundToBulkProxy)[]) => void;
}) => {
  const hasProxyVisibilityFF = useHasFF('proxy_visibility_ff');
  const isStudioOrReport = !isNil(useRecoilValue(analysisViewRefIdState));
  const proxySetId = useRecoilValue(proxySetIdState);
  const hasSingleProxy = investments.length === 1;
  const singleInvestment = hasSingleProxy ? investments[0] : undefined;
  const investmentIds = investments.map((investment) => investment.id);
  const proxyDataByFund = useProxyDataQuery(investmentIds, proxySetId);

  const [isProxiesUpdating, setIsProxiesUpdating] = useState(false);

  const holdingsProxyDataByFund = useMemo(
    () =>
      Object.fromEntries(Object.entries(proxyDataByFund).map(([fundId, { holdings }]) => [fundId, holdings])) as Record<
        string,
        DetailedProxyMetadata
      >,
    [proxyDataByFund],
  );

  const { onClose } = useContext(SidePanelOverlay.Context);

  const proceedMessage = 'Apply holdings proxy';
  const [selectedProxy, setSelectedProxy] = useState<SelectedProxy | null>(() => {
    const proxy = investmentIds[0] ? holdingsProxyDataByFund[investmentIds[0]] : undefined;
    return proxy
      ? {
          id: proxy.proxyId,
          name: proxy.proxyName,
        }
      : null;
  });

  const onSaveProxy = useCallback(async () => {
    if (!selectedProxy) {
      return;
    }

    setIsProxiesUpdating(true);

    try {
      const results = await bulkSetProxy(
        investments.map((investment) => ({
          proxyId: selectedProxy.id,
          fundId: investment.id,
          proxyType: null!,
          proxyVariant: 'HOLDING',
          proxySetId: proxySetId ?? null,
          source: 'APP',
          extrapolate: false,
          numLags: 0,
          proxyOrder: 0,
        })),
      );

      await invalidate(
        'investmentsHoldingsProxy',
        investments.map(({ id }) => id),
      );

      onProxyChange?.(
        results.content.map((proxyMeta) => ({ ...proxyMeta, proxyName: selectedProxy.name })),
        [],
      );

      Notifications.notify(
        `${investments.length} ${hasSingleProxy ? 'proxy' : 'proxies'} updated successfully.`,
        NotificationType.SUCCESS,
      );
    } catch (e) {
      Notifications.notify('Failed to update proxies.', NotificationType.ERROR);
    } finally {
      setIsProxiesUpdating(false);
      onClose();
    }
  }, [hasSingleProxy, investments, onClose, onProxyChange, proxySetId, selectedProxy]);

  const removeHoldingProxyProps = useMemo(() => {
    const investment = investments[0];

    if (!investment) {
      return {};
    }

    const proxyData = holdingsProxyDataByFund[investment.id];
    if (!hasSingleProxy || !proxyData || !proxyData.proxyId) {
      return {};
    }

    const removeHoldingProxy = async () => {
      setIsProxiesUpdating(true);
      try {
        await removeFundProxy(investment.id, proxySetId, 'HOLDING');
        Notifications.notify(
          hasProxyVisibilityFF && isStudioOrReport
            ? 'Proxy successfully reset.'
            : 'Proxy settings successfully removed.',
          NotificationType.SUCCESS,
        );
        await invalidate('investmentsHoldingsProxy', [investment.id]);
        onProxyChange?.([proxyData], []);
      } catch (e) {
        onProxyChange?.([], [investment]);
        Notifications.notify('Failed to remove proxy.', NotificationType.ERROR);
      } finally {
        onClose();
        setIsProxiesUpdating(false);
      }
    };

    return {
      secondaryDisabled: isProxiesUpdating,
      secondaryLabel: hasProxyVisibilityFF && isStudioOrReport ? 'Reset Proxy' : 'Remove Proxy',
      onSecondaryClick: removeHoldingProxy,
      secondaryDestructive: true,
    };
  }, [
    hasProxyVisibilityFF,
    hasSingleProxy,
    holdingsProxyDataByFund,
    investments,
    isProxiesUpdating,
    isStudioOrReport,
    onClose,
    onProxyChange,
    proxySetId,
  ]);

  return (
    <section>
      {isProxiesUpdating && (
        <div className="absolute z-intercom-front-2 top-0 left-0 size-full flex place-items-center bg-white bg-opacity-30 opacity-0 animate-[fade-in-opacity_1s_ease-in-out_1s_1_normal_forwards]">
          <Loading pageLoader />
        </div>
      )}
      <ProxyPickerHeader
        title={HEADER_TITLE}
        subtitle={
          hasProxyVisibilityFF && isStudioOrReport
            ? `Applies to all instances of ${singleInvestment?.name} in this view.`
            : MANAGE_DATA_SUBTITLE
        }
      />
      <div className={cn('px-[24px] py-[20px]')}>
        {singleInvestment && (
          <SingleInvestmentInfo
            investment={singleInvestment}
            proxyData={investments[0] ? holdingsProxyDataByFund[investments[0].id] : undefined}
          />
        )}

        <div className={cn('mt-[24px]')}>
          <div className={cn('font-bold mb-[6px]')}>
            Select an investment to use as holdings proxy
            <span className={cn('ml-[6px]')}>
              <HoldingsProxyInfoIcon />
            </span>
          </div>

          <div className={cn('p-[9px] rounded border border-solid border-venn-grey')}>
            <HoldingsProxySearch selectedProxy={selectedProxy} setSelectedProxy={setSelectedProxy} />
          </div>
        </div>

        {selectedProxy && (
          <div className={cn('mt-[24px]')}>
            <HoldingsProxySummary
              selectedProxy={selectedProxy}
              investments={investments}
              proxyDataByFund={holdingsProxyDataByFund}
            />
          </div>
        )}
      </div>

      <SidePanelOverlay.Footer
        cancelLabel={
          <div className="flex items-center gap-[4.5px] text-[12px]">
            <Icon type="angle-left" /> BACK
          </div>
        }
        onCancel={isProxiesUpdating ? undefined : onClose}
        onPrimaryClick={onSaveProxy}
        primaryLabel={proceedMessage}
        primaryDisabled={!selectedProxy || isProxiesUpdating}
        fixed
        {...removeHoldingProxyProps}
      />
    </section>
  );
};

export const BulkHoldingsProxyPicker = withSuspense(
  <>
    <ProxyPickerHeader title={HEADER_TITLE} subtitle={undefined} />
    <ShimmerBlock />
  </>,
  BulkHoldingsProxyPickerInternal,
);
